import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import { HiDotsVertical } from 'react-icons/hi';
import Select from 'react-select';
import { HSpace, VSpace } from '@/components/spacing';
import { Container } from '@/features/details/sessions-type-chip';
import { EditorContext } from '@/features/editor/context/editor-context';
import { countryCodeToFlag, countryCodes } from '@/utils/country-codes';

export const PREVIEW_ACTIONS_POPOVER_ID = `preview-actions-popover`;

export function PreviewActions() {
  const {
    devicePreview: {
      addCartItem,
      removeAllCartItems,
      sendChangeLocale,
      editorState,
    },
    deviceNavigation: { previewUrl },
  } = useContext(EditorContext);
  const [open, setOpen] = useState(false);
  const [marketOpen, setMarketOpen] = useState(false);
  const selectedMarket = editorState.currentLocale || null;

  const marketOptions = countryCodes.map((c) => ({
    value: c.key,
    label: `${countryCodeToFlag(c.key)} ${c.name}`,
  }));

  const onAddCartItem = () => {
    addCartItem();
  };

  const onRemoveAllCartItems = () => {
    removeAllCartItems();
  };

  const onMarketChange = (market: string) => {
    sendChangeLocale(market, previewUrl);
    setMarketOpen(false);
  };

  return (
    <Popover
      onClickOutside={() => setOpen(!open)}
      isOpen={open}
      containerStyle={{ zIndex: `29` }}
      positions={[`bottom`]}
      content={({ position, childRect, popoverRect }) => (
        <ArrowContainer
          position={position}
          childRect={childRect}
          popoverRect={popoverRect}
          arrowColor="white"
          arrowSize={10}
          arrowStyle={{ margin: `1px 1px 1px 3px` }}
        >
          <PopoverContainer>
            <Header>SIMULATOR ACTIONS</Header>
            <Divider />
            <ActionItem onClick={() => onAddCartItem()}>
              Add a Cart Item
            </ActionItem>
            <Divider />
            <ActionItem onClick={() => onRemoveAllCartItems()}>
              Remove all Cart Items
            </ActionItem>
            <Divider />
            <VSpace value={0.5} />
            <Header>MARKET</Header>

            <Select
              instanceId="preview-market-select"
              options={marketOptions}
              placeholder="Choose Market"
              value={
                selectedMarket
                  ? marketOptions.find((o) => o.value === selectedMarket)
                  : null
              }
              onChange={(option) => {
                if (!option) return;
                sendChangeLocale(option.value, previewUrl);
              }}
              styles={marketSelectStyles}
            />

            {marketOpen && (
              <MarketList>
                {countryCodes.map((c) => (
                  <MarketItem key={c.key} onClick={() => onMarketChange(c.key)}>
                    {countryCodeToFlag(c.key)} {c.name}
                  </MarketItem>
                ))}
              </MarketList>
            )}
          </PopoverContainer>
        </ArrowContainer>
      )}
    >
      <Container id={PREVIEW_ACTIONS_POPOVER_ID}>
        <HSpace />
        <ChipWrap onClick={() => setOpen(!open)}>
          <HiDotsVertical />
        </ChipWrap>
      </Container>
    </Popover>
  );
}

const PopoverContainer = styled.div`
  user-select: none;
  display: flex;
  flex-direction: column;
  background-color: white;
  padding: 1.6rem 0px;
  max-width: 250px;
  border-radius: 18px;
  width: 250px;
  border: solid 1px rgba(151, 151, 151, 0.1);
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.09), 0 1px 4px 0 rgba(7, 6, 6, 0.04);
`;

const ChipWrap = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;

  cursor: pointer;
  background: white;

  font-size: 1.2rem;
  font-weight: 500;
  font-family: 'JetBrains Mono', serif;
  user-select: none;

  color: #545454;
  border-radius: 2rem;
  padding: 1rem;

  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.09);

  :active {
    opacity: 1;
  }
`;

const Header = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #9f9f9f;
  padding: 0px 1.6rem;
  margin: 0.2rem 0rem 0.6rem 0.3rem;
`;

const ActionItem = styled.div`
  font-size: 1.3rem;
  font-weight: 500;
  color: #000;
  padding: 1rem 1.6rem;
  cursor: pointer;
  white-space: nowrap;
  margin-left: 0.6rem;

  &:hover {
    opacity: 0.7;
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: #dcdcdc;
  opacity: 0.65;
  margin: 0.5rem 1.6rem 0.5rem 1.6rem;
`;

const MarketList = styled.div`
  margin-top: 0.6rem;
  max-height: 180px;
  overflow-y: auto;

  border-radius: 8px;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.08);
`;

const MarketItem = styled.div`
  padding: 0.8rem 0.5rem;
  font-size: 1.25rem;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #f3f3f3;
  }
`;

const marketSelectStyles = {
  control: (base: any) => ({
    ...base,
    borderRadius: `8px`,
    border: `none`,
    boxShadow: `none`,
    cursor: `pointer`,
    padding: `2px 6px`,
    minHeight: `42px`,
    fontSize: `1.5rem`,
    fontWeight: 500,
  }),
  indicatorSeparator: () => ({
    display: `none`,
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: `0px`,
  }),
  singleValue: (base: any) => ({
    ...base,
    paddingLeft: `10px`,
  }),
  menu: (base: any) => ({
    ...base,
    borderRadius: `8px`,
    marginTop: `6px`,
    overflow: `hidden`,
    border: `1px solid rgba(0,0,0,0.08)`,
  }),
  menuList: (base: any) => ({
    ...base,
    maxHeight: `220px`,
    padding: 0,
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: `1.5rem`,
    padding: `10px 12px`,
    cursor: `pointer`,
    background: state.isSelected ? `#f3f3f3` : `white`,
    fontWeight: state.isSelected ? 700 : 500,
    color: `#000`,
    ':hover': {
      background: `#f3f3f3`,
    },
  }),
  placeholder: (base: any) => ({
    ...base,
    color: `#000`,
    opacity: 0.7,
  }),
};
